/**
 * Combined sign-in / sign-up.
 *
 * The router mounts this page only while nobody is signed in, so a non-null user
 * here means the form just succeeded and we can hand over to the map.
 */

import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { ApiError, type Level } from '../api'
import { ErrorNote, LevelPicker, Spinner } from '../components/ui'
import { useAuth } from '../store/auth'

type Mode = 'signin' | 'signup'

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server. Is the API running?'
}

export default function Login() {
  const { user, signIn, signUp } = useAuth()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [level, setLevel] = useState<Level>('N5')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signup = mode === 'signup'

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (signup) {
        await signUp({
          email: email.trim(),
          password,
          display_name: displayName.trim(),
          target_level: level,
        })
      } else {
        await signIn(email.trim(), password)
      }
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  function switchTo(next: Mode) {
    setMode(next)
    setError(null)
  }

  // App.tsx owns the post-login redirect so it can honour the URL the visitor was
  // originally reaching for. This is just a guard for a direct render of /login.
  if (user) return <Navigate to="/map" replace />

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <aside className="dot-grid hidden flex-col justify-center gap-10 border-r border-ink-700 px-14 lg:flex lg:w-1/2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-500">
            Nihongo Visualizer
          </div>
          <h1 className="mt-3 max-w-lg text-3xl font-bold leading-tight text-slate-100">
            Kanji are not two thousand pictures. They are a few hundred parts, reused.
          </h1>
        </div>

        <div className="flex items-center gap-8">
          <div className="jp select-none text-[128px] font-semibold leading-none text-slate-100">休</div>
          <div className="space-y-2">
            <div className="jp text-lg font-semibold text-slate-200">休 = 亻 person + 木 tree</div>
            <p className="max-w-xs text-sm leading-relaxed text-ink-500">
              A person beside a tree — <span className="font-semibold text-brand-500">rest</span>. Learn the parts
              once and every kanji built from them costs a fraction as much to remember.
            </p>
          </div>
        </div>

        <ul className="space-y-2 text-sm text-ink-500">
          <li>An interactive map of how every kanji decomposes.</li>
          <li>Spaced repetition that schedules what you are about to forget.</li>
          <li>Quizzes on meaning, reading and structure, with a scorecard.</li>
        </ul>
      </aside>

      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="card w-full max-w-md p-6 sm:p-8">
          <div className="mb-6 flex gap-1 rounded-xl border border-ink-700 bg-ink-950/60 p-1">
            <button
              type="button"
              aria-pressed={!signup}
              onClick={() => switchTo('signin')}
              className={`btn flex-1 ${signup ? 'text-ink-500 hover:text-slate-200' : 'bg-ink-800 text-slate-100'}`}
            >
              Sign in
            </button>
            <button
              type="button"
              aria-pressed={signup}
              onClick={() => switchTo('signup')}
              className={`btn flex-1 ${signup ? 'bg-ink-800 text-slate-100' : 'text-ink-500 hover:text-slate-200'}`}
            >
              Create account
            </button>
          </div>

          <h2 className="text-xl font-bold text-slate-100">
            {signup ? 'Start your first level' : 'Welcome back'}
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            {signup
              ? 'Pick the JLPT level you are aiming at — you can change it any time.'
              : 'Your progress, flashcards and quiz history are waiting.'}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="label" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                className="input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {signup && (
              <div>
                <label className="label" htmlFor="login-name">
                  Display name
                </label>
                <input
                  id="login-name"
                  className="input"
                  type="text"
                  required
                  maxLength={40}
                  autoComplete="nickname"
                  placeholder="How the scoreboard should list you"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="label" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                className="input"
                type="password"
                required
                minLength={signup ? 8 : undefined}
                autoComplete={signup ? 'new-password' : 'current-password'}
                placeholder={signup ? 'At least 8 characters' : '••••••••'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {signup && (
              <fieldset>
                <legend className="label">Target level</legend>
                <LevelPicker value={level} onChange={setLevel} />
                <p className="mt-2 text-xs text-ink-500">N5 is the beginner level; N1 is the hardest.</p>
              </fieldset>
            )}

            {error && <ErrorNote>{error}</ErrorNote>}

            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {signup ? 'Create account' : 'Sign in'}
            </button>

            {busy && <Spinner label={signup ? 'Creating your account…' : 'Signing in…'} />}
          </form>

          <p className="mt-6 text-center text-xs text-ink-500">
            {signup ? 'Already registered? ' : 'No account yet? '}
            <button
              type="button"
              className="font-semibold text-brand-500 hover:underline"
              onClick={() => switchTo(signup ? 'signin' : 'signup')}
            >
              {signup ? 'Sign in instead' : 'Create one'}
            </button>
          </p>
        </div>
      </main>
    </div>
  )
}
